/**
 * Clock-driven values for the modeled operations layers. The static half of
 * each dataset (capacities, cabinet positions, bin sites) is generated once
 * by scripts/build-ops-mock.mjs from real campus geometry; everything that
 * should move with the day is computed here from the wall clock so nothing
 * shipped can go stale. All of it is scenario-tier and labelled as such.
 *
 * Pure functions, deterministic per (id, minute) via seedNoise — a building
 * does not flicker between two renders, and the API would agree if asked.
 */
import { seedNoise } from "@chula/shared/seedNoise";

export type BuildingUse = "academic" | "hospital" | "residential" | "canteen" | "library" | "sports";

const gaussian = (h: number, centre: number, sigma: number) => Math.exp(-((h - centre) ** 2) / (2 * sigma * sigma));

/**
 * Fraction of capacity present at fractional hour `h`, before jitter.
 * Academic buildings fill for a 09:00–16:00 teaching day with a lunch dip;
 * residential inverts (people are home at night); hospitals never empty;
 * canteens spike at meals; sports peak after class.
 */
export function occupancyCurve(use: BuildingUse, h: number, isWeekend: boolean): number {
  const wk = isWeekend ? 0.25 : 1;
  switch (use) {
    case "residential":
      return 0.85 - 0.55 * gaussian(h, 13, 4);
    case "hospital":
      return 0.55 + 0.35 * gaussian(h, 11, 4);
    case "canteen":
      return wk * (0.9 * gaussian(h, 12.2, 0.8) + 0.5 * gaussian(h, 7.6, 0.6) + 0.55 * gaussian(h, 17.8, 0.9));
    case "library":
      return wk * (0.75 * gaussian(h, 14.5, 3.5) + 0.35 * gaussian(h, 19.5, 1.5));
    case "sports":
      return wk * (0.7 * gaussian(h, 17.5, 1.8) + 0.3 * gaussian(h, 7.5, 1));
    case "academic":
    default:
      return wk * Math.max(0, 0.82 * gaussian(h, 12.5, 3.2) - 0.18 * gaussian(h, 12.2, 0.5));
  }
}

/** People in the building right now. `tempo` is the academic-calendar multiplier (1 = semester). */
export function occupiedNow(
  id: string,
  capacity: number,
  use: BuildingUse,
  now: Date,
  tempo = 1,
): number {
  const h = now.getHours() + now.getMinutes() / 60;
  const day = now.getDay();
  const isWeekend = day === 0 || day === 6;
  const minuteBucket = Math.floor(now.getTime() / 60_000);
  const jitter = 0.85 + seedNoise(id, minuteBucket >> 4) * 0.3; // stable for ~16 min
  const frac = Math.min(1, occupancyCurve(use, h, isWeekend) * tempo * jitter);
  return Math.round(capacity * frac);
}

// ── Parking ─────────────────────────────────────────────────────────────────
// Same commute curve lib/parking.ts uses for the panel, applied per lot with a
// per-lot bias so a hospital car park and a rear staff lot don't fill alike.
export function parkingOccupiedNow(id: string, capacity: number, curvePct: number, now: Date): number {
  const bias = 0.8 + seedNoise(id, 7) * 0.4; // 0.8–1.2
  const minuteBucket = Math.floor(now.getTime() / 60_000);
  const wobble = 0.95 + seedNoise(id, minuteBucket >> 3) * 0.1;
  return Math.min(capacity, Math.round((capacity * curvePct * bias * wobble) / 100));
}

// ── Internal traffic ────────────────────────────────────────────────────────
/** Modeled speed (km/h) on a campus segment. Gate approaches jam at commute peaks. */
export function internalSpeedNow(id: string, freeFlowKmh: number, gateApproach: boolean, now: Date): number {
  const h = now.getHours() + now.getMinutes() / 60;
  const day = now.getDay();
  if (day === 0 || day === 6) return Math.round(freeFlowKmh * (0.9 + seedNoise(id, 1) * 0.1));
  const amPeak = gaussian(h, 8.25, 0.55);
  const pmPeak = gaussian(h, 17, 0.7);
  const congestion = gateApproach ? 0.75 * Math.max(amPeak, pmPeak) : 0.3 * Math.max(amPeak, pmPeak);
  const minuteBucket = Math.floor(now.getTime() / 60_000);
  const wobble = 0.9 + seedNoise(id, minuteBucket >> 2) * 0.2;
  return Math.max(3, Math.round(freeFlowKmh * (1 - congestion) * wobble));
}

// ── Trash ───────────────────────────────────────────────────────────────────
export const PICKUP_TIMES_H = [6.5, 13, 19.5] as const;

/** Fill 0–100 %. Rises from the last pickup; food-waste and market bins fill faster. */
export function binFillNow(id: string, binType: string, site: string, now: Date): number {
  const h = now.getHours() + now.getMinutes() / 60;
  const lastPickup = [...PICKUP_TIMES_H].filter((t) => t <= h).pop() ?? PICKUP_TIMES_H[PICKUP_TIMES_H.length - 1] - 24;
  const sinceH = h - lastPickup;
  const busy = /ตลาด|market|โรงอาหาร|canteen|มิตรทาวน์/i.test(site) ? 1.6 : 1;
  const rate = (binType === "food-waste" ? 14 : binType === "recycling" ? 7 : 10) * busy; // % per hour
  const bias = 0.7 + seedNoise(id, 3) * 0.6;
  return Math.min(100, Math.round(sinceH * rate * bias));
}

export function nextPickupLabel(now: Date): string {
  const h = now.getHours() + now.getMinutes() / 60;
  const next = PICKUP_TIMES_H.find((t) => t > h) ?? PICKUP_TIMES_H[0];
  const hh = Math.floor(next), mm = Math.round((next - hh) * 60);
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}${next <= h ? " (tomorrow)" : ""}`;
}
